import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { required, numericRange } from '@mhub/web-validations';
import {
  Button,
  Container,
  Dimmer,
  Form,
  Icon,
  Label,
  Loader,
  Popup,
  Table,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import AppForm from '../components/AppForm';
import CURRENCIES from '../constants/currencies';
import DeleteConfirm from '../containers/modals/DeleteConfirm';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';

class Currencies extends Component {
  static propTypes = {
    allowSwitchingDefault: PropTypes.bool,
    companyDefaultCurrency: PropTypes.string.isRequired,
    isForeignCompany: PropTypes.bool,
    currenciesProvided: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    data: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    loading: PropTypes.bool,
    onDefaultAdd: PropTypes.func.isRequired,
    onDefaultDelete: PropTypes.func.isRequired,
    onDefaultMakeDefault: PropTypes.func,
    onDefaultSave: PropTypes.func.isRequired,
  }
  static defaultCurrency = {
    selectedCurrency: null,
    newName: null,
    newCode: '',
    newConversionRate: '',
    isEditing: false,
  }
  constructor(props) {
    super(props);
    const state = this.getPropsToState(props);
    this.validationForEdit = {
      conversionRatePoint: createRef(),
    };
    this.validationForCreate = {
      newCode: createRef(),
      newConversionRate: createRef(),
    };
    this.state = {
      dataToBeDelete: {},
      isEditing: false,
      isNew: false,
      showDeleteConfirm: false,
      ...state,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = this.getPropsToState(nextProps);
    this.setState({
      ...state,
    });
  }
  convertRateToDecimal(rate) {
    return rate / 1000;
  }
  filterCurrencies = (existingCurrencies = []) => {
    let currencies = CURRENCIES;
    if (this.props.currenciesProvided) currencies = this.props.currenciesProvided;
    // Remove currency option that is already in existingCurrencies
    const filtered = currencies.filter(c => existingCurrencies.every(e => e.code !== c.value));
    return filtered;
  }
  getPropsToState = (props) => {
    const allowSwitchingDefault = props.allowSwitchingDefault;
    const data = this.transformData(props.data);
    const loading = props.loading;
    const currencyOptions = this.filterCurrencies(data);

    return {
      allowSwitchingDefault,
      currencyOptions,
      data,
      loading,
      ...Currencies.defaultCurrency,
    };
  }
  handleCancelDelete = (e) => {
    // Handler for confirmation dialog
    e.preventDefault();
    this.setState({
      showDeleteConfirm: false,
    });
  }
  handleConfirmDelete = (e) => {
    // Handler for confirmation dialog
    e.preventDefault();
    this.setState({
      showDeleteConfirm: false,
    }, () => {
      this.onDelete(this.state.dataToBeDelete);
    });
  }
  handleEditChange = (index, event, { value, name }) => {
    event.preventDefault();
    const extraState = {};
    if (name === 'isEdit') {
      // toggle isEditing flag to allow only one edit at a time
      extraState.isEditing = !this.state.isEditing;
    }

    const { data } = this.state;
    data[index] = Object.assign({}, data[index], { [name]: value });
    this.setState({
      data,
      ...extraState,
    });
  }
  handleInputChange = (e, d) => {
    this.setState({
      [d.name]: d.value,
    });
  }
  handleMakeDefault = (e, d) => {
    e.preventDefault();
    this.props.onDefaultMakeDefault(null, { value: d.value });
  }
  handleSelectChange = (e, d) => {
    // value is the code of the currency
    const selected = CURRENCIES.find(val => val.value === d.value);
    this.setState({
      newCode: selected.value,
      newName: selected.text,
      selectedCurrency: selected.value,
    });
  }
  handleSubmit = (e) => {
    e.preventDefault();
  }
  isShowFirstCurrencyMsg() {
    return this.state.data.length === 0 && this.state.isNew;
  }
  onClickAdd = (e) => {
    e.preventDefault();
    this.setState({ isNew: true });
  }
  onClickDelete = (index, e, data) => {
    e.preventDefault();
    this.setState({
      dataToBeDelete: {
        index,
        ...data,
      },
      showDeleteConfirm: true,
    });
  }
  onCancelCreate = (e) => {
    e.preventDefault();
    this.setState({
      isNew: false,
    });
  }
  onCreate = (e) => {
    e.preventDefault();
    return AppForm.validate(this.validationForCreate, this.state)
      .then((validations) => {
        const value = {
          name: this.state.newName,
          code: this.state.newCode,
          conversionRatePoint: this.state.newConversionRate * 1000,
          isDefault: this.state.data.length === 0,
        };
        this.props.onDefaultAdd(validations, { value });
      });
  }
  onDelete = (dataToBeDelete = {}) => {
    this.props.onDefaultDelete(null, { value: dataToBeDelete });
  }
  onSave = (index, e) => {
    e.preventDefault();
    const newData = this.state.data[index];
    const value = {
      code: newData.code,
      conversionRatePoint: newData.conversionRatePoint * 1000,
      id: newData.id,
      isDefault: newData.isDefault,
      index,
    };
    return AppForm.validate(this.validationForEdit, value)
      .then((validations) => {
        this.props.onDefaultSave(validations, { value });
      });
  }
  transformData = (data = []) => {
    // Transform data prior to being rendered
    // Adds decimal point to conversionRatePoint of integer type
    // Adds isEdit flag to indicate if it is being edited
    const result = data.map((d) => {
      const dataToBeTransformed = {
        isEdit: false,
        conversionRatePoint: this.convertRateToDecimal(d.conversionRatePoint),
      };
      const currency = Object.assign({}, d, dataToBeTransformed);
      return currency;
    });
    return result;
  }
  isDeletable(value) {
    const { isDefault, code } = value;
    const { allowSwitchingDefault } = this.state;
    const { companyDefaultCurrency } = this.props;
    return !isDefault && allowSwitchingDefault && (code !== companyDefaultCurrency);
  }
  renderActionsColumn(isEdit, isEditing, index, value) {
    const { isForeignCompany, t } = this.props;
    // To render actions button according to the edit state of the rows
    // If is editing, only show button for the row that is being edited and hide buttons on other rows
    // If not editing, show button for edit and delete for all rows
    // Delete button is not available for default currency
    const toSaveAction = (
      <span>
        <Popup
          trigger={
            <Button
              positive
              className="ui compact icon primary button"
              onClick={
                e => this.onSave(index, e)
              }
            >
              <Icon name="save" />
            </Button>
          } content={t('Save', { ns: TRANSLATION_FILES.translation })}
        />
        <Popup
          trigger={
            <Button
              negative
              className="ui compact icon primary button"
              onClick={
                e => this.handleEditChange(
                  index,
                  e,
                  { value: false, name: 'isEdit' },
                )
              }
            >
              <Icon name="close" />
            </Button>
          } content={t('Cancel', { ns: TRANSLATION_FILES.translation })}
        />
      </span>
    );

    const toEditAction = (
      <span>
        <Popup
          trigger={
            <Button
              className="ui compact icon primary button"
              disabled={isForeignCompany}
              onClick={
                e => this.handleEditChange(
                  index,
                  e,
                  { value: true, name: 'isEdit' },
                )
              }
            >
              <Icon name="edit" />
            </Button>
          } content={t('Edit', { ns: TRANSLATION_FILES.translation })}
        />
        {
          this.isDeletable(value) ? (
            <Popup
              trigger={
                <Button
                  negative
                  className="ui compact icon primary button"
                  onClick={
                    e => this.onClickDelete(
                      index,
                      e,
                      value,
                    )
                  }
                >
                  <Icon name="trash" />
                </Button>
              } content={t('Delete', { ns: TRANSLATION_FILES.translation })}
            />
          ) :
          null
        }
      </span>
    );

    if (isEditing) {
      if (isEdit) {
        return toSaveAction;
      }
    } else {
      return toEditAction;
    }
    return null;
  }
  render() {
    const { isForeignCompany, t } = this.props;
    return (
      <Container>
        <DeleteConfirm
          open={this.state.showDeleteConfirm}
          onClose={this.handleCancelDelete}
          onConfirm={this.handleConfirmDelete}
          name={(this.state.dataToBeDelete && this.state.dataToBeDelete.name) || t('Currency')}
        />
        <Dimmer
          inverted style={{ zIndex: 20 }}
          active={this.state.loading && this.state.data.length > 0}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <AppForm
          onSubmit={this.handleSubmit}
          ref={(ref) => { this.form = ref; }}
          as="div"
        >
          <Table
            striped
            selectable
            padded
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell colSpan={5} textAlign="left">
                  <Button
                    disabled={isForeignCompany}
                    primary
                    size="small"
                    icon="add"
                    content={t('Add', { ns: TRANSLATION_FILES.translation })}
                    onClick={this.onClickAdd}
                  />
                </Table.HeaderCell>
              </Table.Row>
              <Table.Row>
                <Table.HeaderCell>{t('Currency')}</Table.HeaderCell>
                <Table.HeaderCell>{t('Code')}</Table.HeaderCell>
                <Table.HeaderCell>{t('Rate')}</Table.HeaderCell>
                <Table.HeaderCell />
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {
                this.state.data.length > 0 ?
                  this.state.data.map((value, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>
                        {value.name}
                      </Table.Cell>
                      <Table.Cell>
                        {value.code}
                      </Table.Cell>
                      <Table.Cell>
                        {
                          value.isEdit ? (
                            <AppForm.Input
                              hideMessage
                              validateLabel={`${value.name} ${t('Conversion rate').toLowerCase()}`}
                              name="conversionRatePoint"
                              value={value.conversionRatePoint}
                              onChange={
                                (e, data) => {
                                  const d = data;
                                  d.name = 'conversionRatePoint';
                                  return this.handleEditChange(
                                    index,
                                    e,
                                    d,
                                  );
                                }
                              }
                              ref={this.validationForEdit.conversionRatePoint}
                              validation={[required, {
                                validate: numericRange,
                                option: {
                                  min: 0.1,
                                },
                              }]}
                              type="number"
                            />
                          ) : value.conversionRatePoint
                        }
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        { value.isDefault ? (
                          <Label color="blue" basic horizontal>
                            {t('Default')}
                          </Label>
                        ) : null }
                        {
                          !value.isDefault && this.state.allowSwitchingDefault ? (
                            <Button
                              value={value}
                              primary
                              compact
                              size="tiny"
                              onClick={this.handleMakeDefault}
                            >
                              {t('Make Default')}
                            </Button>
                          ) : null
                        }
                      </Table.Cell>
                      <Table.Cell>
                        {
                          this.renderActionsColumn(value.isEdit, this.state.isEditing, index, value)
                        }
                      </Table.Cell>
                    </Table.Row>
                  ))
                :
                  <Table.Row>
                    <Table.Cell colSpan={5} textAlign="center">
                      {
                        this.state.loading ?
                          <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
                        : `${t('No currency found')}.`
                      }
                    </Table.Cell>
                  </Table.Row>
              }
            </Table.Body>
            {
              this.state.isNew ? (
                <Table.Body>
                  <Table.Row active>
                    <Table.Cell>
                      <AppForm.Validate
                        hideMessage
                        validation={[required]}
                        validateLabel={t('Currency')}
                        ref={this.validationForCreate.newCode}
                        onChange={this.handleSelectChange}
                      >
                        <Form.Select
                          text={this.state.selectedCurrency ? null : t('Select Currency')}
                          autoComplete="off"
                          fluid
                          name="selectedCurrency"
                          options={this.state.currencyOptions}
                          search
                          selectOnBlur={false}
                          value={this.state.selectedCurrency}
                          onChange={this.handleSelectChange}
                        />
                      </AppForm.Validate>
                    </Table.Cell>
                    <Table.Cell>
                      {
                        this.state.newCode
                      }
                    </Table.Cell>
                    <Table.Cell>
                      <AppForm.Input
                        hideMessage
                        validateLabel={t('Conversion rate')}
                        name="newConversionRate"
                        value={this.state.newConversionRate}
                        onChange={this.handleInputChange}
                        ref={this.validationForCreate.newConversionRate}
                        validation={[required, {
                          validate: numericRange,
                          option: {
                            min: 0.1,
                          },
                        }]}
                        type="number"
                      />
                    </Table.Cell>
                    <Table.Cell />
                    <Table.Cell>
                      <Popup
                        trigger={
                          <Button
                            positive
                            className="ui compact icon primary button"
                            onClick={this.onCreate}
                          >
                            <Icon name="plus" />
                          </Button>
                        } content={t('Create', { ns: TRANSLATION_FILES.translation })}
                      />
                      <Popup
                        trigger={
                          <Button
                            negative
                            className="ui compact icon primary button"
                            onClick={this.onCancelCreate}
                          >
                            <Icon name="close" />
                          </Button>
                        } content={t('Cancel', { ns: TRANSLATION_FILES.translation })}
                      />
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              ) : null
            }
            {
              this.isShowFirstCurrencyMsg() ? (
                <Table.Body>
                  <Table.Row>
                    <Table.Cell colSpan={5} textAlign="center">
                    {t('The first currency you set will be the base currency')}
                    {
                      this.state.allowSwitchingDefault ? '.' :
                      ` ${t('and cannot be edited once saved')}.`
                    }
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>

              ) : null
            }
          </Table>
        </AppForm>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(Currencies);



// WEBPACK FOOTER //
// ./src/components/Currencies.jsx